import {
  PromptBannerCard,
  listingPromptBannerSharedInnards,
  promptBannerPrimaryButtonStyle,
  useMintListingPromptMinHeight,
} from 'app/components/prompt-banner-card';
import { Column, Row } from 'app/design/primitives/box';
import { Text } from 'app/design/primitives/text';
import { useTranslation } from 'react-i18next';

import { Pressable, type StyleProp, type ViewStyle } from 'react-native';
import { Button } from 'app/design/primitives/button';
import { StorageKey, useStoredValue } from 'app/lib/storage';
import { useNavigation } from 'app/hooks/navigation';
import { useReferralPrize202506Available, useReferralPrizeAvailable } from 'app/network/promotions';
import { useUISize } from 'app/utils/web-helpers';
import { AddressIcon6 } from 'app/design/icons/address-icon-6';
import { createTheme as createDSTheme } from 'app/shopify';
const theme = createDSTheme();

export const useInviteOthersPrompt = () => {
  const referralPrize202506Available = useReferralPrize202506Available();
  const referralPrizeAvailable = useReferralPrizeAvailable();

  const [isComponentVisible] = useStoredValue<boolean>(StorageKey.InviteOthersPromptVisible, true);
  return (referralPrize202506Available || referralPrizeAvailable) && isComponentVisible;
};

export function InviteOthersPrompt({
  style,
}: {
  style?: StyleProp<ViewStyle>;
  useImage?: boolean;
  /** Shorter cropped illustration for billboard banner row next to mint cards. */
  compactImage?: boolean;
}) {
  const { t } = useTranslation('all-listings');
  const navigation = useNavigation();
  const { isSmallUI } = useUISize();
  const minHeight = useMintListingPromptMinHeight();

  const [isComponentVisible] = useStoredValue<boolean>(StorageKey.InviteOthersPromptVisible, true);

  if (!isComponentVisible) {
    return null;
  }

  const goToInvite = () => {
    navigation.navigate('invite', undefined, { pop: true });
  };

  return (
    <Column
      position="relative"
      style={[
        {
          width: '100%',
          paddingHorizontal: theme.spacing['spacing.general.x2'],
          marginTop: theme.spacing['spacing.general.x2'],
        },
        style,
      ]}
      width="100%"
    >
      <Pressable onPress={goToInvite} style={{ width: '100%' }}>
        <PromptBannerCard
          {...listingPromptBannerSharedInnards}
          backgroundColor={theme.colors['surface.default']}
          style={{
            minHeight,
            borderWidth: 1,
            borderColor: theme.colors['surface.inverse.tertiary-neutral'],
          }}
        >
          <Row alignItems="center" gap="spacing.general.x2" paddingRight="spacing.general.x12">
            <Text fontSize={isSmallUI ? 18 : 32}>📣</Text>
            <Text flex={1} fontWeight="700" textAlign="left" variant={isSmallUI ? 'bodyBase' : 'bodyLarge'}>
              {t('inviteOthersPrompt.title')}
            </Text>
          </Row>
          <Text textAlign="left" variant="bodyBase">
            {t('inviteOthersPrompt.description')}
          </Text>
          <Button justifyContent="flex-start" onPress={goToInvite} style={promptBannerPrimaryButtonStyle}>
            <Row alignItems="center" gap="spacing.general.x2">
              <AddressIcon6 fillColor="transparent" color={theme.colors['surface.default']} width={28} height={28} />
              <Text color="surface.default" variant="bodyBase">
                {t('inviteOthersPrompt.ctaButton')}
              </Text>
            </Row>
          </Button>
        </PromptBannerCard>
      </Pressable>
    </Column>
  );
}
